/**
 * Custom hook for Assembly Form Dialog
 * Manages state, effects, and handlers for the assembly form
 */

import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  normalizeModelName,
  stringsMatch,
  calculateYearsFromDates,
  calculateCalendarDaysFromDates,
  validateYearsInput,
  validateCalendarDaysInput,
  calculateEndDate,
  prepareTimeSinceRecordsForSubmission
} from './assemblyFormUtils';
import {
  computeRemainingSnapshot,
  convertRemainingCanonicalToDisplay,
  isMixedYearsCalendarLimitElapsed
} from '../components/componentFormUtils';

export const useAssemblyForm = ({
  open,
  selectedAssembly,
  selectedAircraft,
  formData,
  setFormData,
  dropdownOptions,
  onRefreshDropdowns
}) => {
  const navigate = useNavigate();
  
  // Track if we've already refreshed dropdowns for this dialog session
  const hasRefreshedRef = React.useRef(false);
  
  // Track if we've auto-populated from aircraft to prevent re-running
  const hasAutoPopulatedRef = React.useRef(false);
  
  // State for assembly type configuration
  const [assemblyTypeConfig, setAssemblyTypeConfig] = React.useState(null);
  const [configLoading, setConfigLoading] = React.useState(false);
  
  // State for Time Since Records
  const [timeSinceRecords, setTimeSinceRecords] = React.useState([]);

  // Refresh dropdowns when dialog opens (only once per open session)
  React.useEffect(() => {
    if (open && onRefreshDropdowns && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      onRefreshDropdowns();
    }
    // Reset when dialog closes
    if (!open) {
      hasRefreshedRef.current = false;
      // Reset timeSinceRecords when dialog closes
      setTimeSinceRecords([]);
    }
  }, [open]); // Only depend on 'open', not onRefreshDropdowns

  // Auto-populate Manufacturer Name and Model Variant from selected Aircraft
  React.useEffect(() => {
    // Reset flag when dialog closes
    if (!open) {
      hasAutoPopulatedRef.current = false;
      return;
    }

    // Only auto-populate for new assemblies (not when editing) and only once per dialog session
    if (open && selectedAircraft && !selectedAssembly && !hasAutoPopulatedRef.current && 
        dropdownOptions.manufacturerNames && dropdownOptions.models && 
        dropdownOptions.manufacturerNames.length > 0 && dropdownOptions.models.length > 0) {
      
      const aircraftManufacturer = selectedAircraft.manufacturer?.trim();
      const aircraftModel = selectedAircraft.model?.trim();

      // Only auto-populate if fields are empty
      if ((aircraftManufacturer || aircraftModel) && !formData.manufacturerNameId && !formData.modelId) {
        let manufacturerNameId = null;
        let modelId = null;

        // Find matching Manufacturer Name (using flexible matching)
        if (aircraftManufacturer) {
          const matchingManufacturerName = dropdownOptions.manufacturerNames.find(mn => 
            stringsMatch(mn.name, aircraftManufacturer)
          );
          if (matchingManufacturerName) {
            manufacturerNameId = matchingManufacturerName._id;
          }
        }

        // Find matching Model (must match both name and manufacturer if manufacturer was found)
        if (aircraftModel) {
          const matchingModel = dropdownOptions.models.find(model => {
            // Use more flexible matching for model names (handles G-280 vs G280)
            const normalizedModelName = normalizeModelName(model.name);
            const normalizedAircraftModel = normalizeModelName(aircraftModel);
            const isNameMatch = normalizedModelName === normalizedAircraftModel || 
                               stringsMatch(model.name, aircraftModel);
            
            if (!isNameMatch) return false;
            
            // If we found a manufacturer, verify the model's manufacturer matches
            if (manufacturerNameId) {
              const modelManufacturerId = typeof model.manufacturer === 'object' && model.manufacturer !== null
                ? String(model.manufacturer._id || model.manufacturer)
                : String(model.manufacturer);
              return modelManufacturerId === String(manufacturerNameId);
            }
            
            // If no manufacturer found, just match by name
            return true;
          });
          
          if (matchingModel) {
            modelId = matchingModel._id;
            // If we didn't find manufacturer by name, try to get it from the model's manufacturer
            if (!manufacturerNameId && matchingModel.manufacturer) {
              const modelManufacturer = typeof matchingModel.manufacturer === 'object' && matchingModel.manufacturer !== null
                ? matchingModel.manufacturer
                : null;
              if (modelManufacturer && modelManufacturer._id) {
                manufacturerNameId = modelManufacturer._id;
              }
            }
          }
        }

        // Update formData if we found matches
        if (manufacturerNameId || modelId) {
          hasAutoPopulatedRef.current = true;
          setFormData(prev => ({
            ...prev,
            ...(manufacturerNameId && { manufacturerNameId }),
            ...(modelId && { modelId })
          }));
        } else {
          // Mark as attempted even if no matches found to prevent re-running
          hasAutoPopulatedRef.current = true;
        }
      }
    }
  }, [open, selectedAircraft, selectedAssembly, dropdownOptions.manufacturerNames, dropdownOptions.models, formData.manufacturerNameId, formData.modelId]);

  // Filter model variants based on selected manufacturer name
  const filteredModelVariants = React.useMemo(() => {
    if (!formData.manufacturerNameId) {
      return [];
    }
    return (dropdownOptions.models || []).filter(model => {
      // Handle both populated (object) and unpopulated (string ID) manufacturer references
      let manufacturerId;
      if (typeof model.manufacturer === 'object' && model.manufacturer !== null) {
        // Populated: get _id from the object
        manufacturerId = model.manufacturer._id ? String(model.manufacturer._id) : String(model.manufacturer);
      } else {
        // Unpopulated: use the ID directly
        manufacturerId = String(model.manufacturer);
      }
      // Compare with selected manufacturerNameId
      return String(formData.manufacturerNameId) === manufacturerId;
    });
  }, [formData.manufacturerNameId, dropdownOptions.models]);

  // Fetch assembly type configuration
  const fetchAssemblyTypeConfig = React.useCallback(async (assemblyTypeId) => {
    if (!assemblyTypeId) {
      setAssemblyTypeConfig(null);
      return;
    }

    setConfigLoading(true);
    try {
      const response = await axios.get(
        `/api/master-data/assembly-type-configurations/assembly-type/${assemblyTypeId}`
      );
      setAssemblyTypeConfig(response.data);
      
      // Merge existing values with configuration columns
      setFormData(prev => {
        const existingValues = prev.valuesAtInstallation || [];
        const configColumns = response.data.columns
          .sort((a, b) => a.order - b.order);
        
        // Create a map of existing values by columnName
        const existingMap = new Map(
          existingValues.map(val => [val.columnName, val])
        );
        
        // Merge: use existing values if available, otherwise create new empty entries
        const mergedValues = configColumns.map(col => {
          const existing = existingMap.get(col.name);
          return existing || {
            columnName: col.name,
            assemblyValue: '',
            airframeValue: ''
          };
        });
        
        return {
          ...prev,
          valuesAtInstallation: mergedValues
          // Keep existing customColumns and customValuesAtInstallation when config exists (both can be used)
        };
      });
    } catch (error) {
      // Configuration not found is acceptable - not all assembly types have configurations
      if (error.response?.status !== 404) {
        console.error('Error fetching assembly type configuration:', error);
      }
      setAssemblyTypeConfig(null);
      // Only clear valuesAtInstallation if we don't have existing values (i.e., new assembly)
      setFormData(prev => {
        // If we have existing values, keep them (might be from editing)
        if (prev.valuesAtInstallation && prev.valuesAtInstallation.length > 0) {
          return prev;
        }
        return {
          ...prev,
          valuesAtInstallation: []
        };
      });
    } finally {
      setConfigLoading(false);
    }
  }, [setFormData]);

  // Fetch configuration when assembly type changes
  React.useEffect(() => {
    if (formData.type) {
      fetchAssemblyTypeConfig(formData.type);
    } else {
      setAssemblyTypeConfig(null);
      setFormData(prev => ({
        ...prev,
        valuesAtInstallation: [],
        customColumns: [],
        customValuesAtInstallation: []
      }));
    }
  }, [formData.type, fetchAssemblyTypeConfig]);

  // Initialize timeSinceRecords from selectedAssembly or formData
  React.useEffect(() => {
    if (open) {
      // First check formData (for editing existing assembly)
      if (formData.timeSinceRecords && Array.isArray(formData.timeSinceRecords) && formData.timeSinceRecords.length > 0) {
        const recordsWithLimit = formData.timeSinceRecords.map(record => {
          // Clean Calendar Days values to ensure they're integers
          let elapsedValue = record.elapsedTime?.value || '';
          let remainingValue = record.remainingTime?.value || '';
          let limitValue = record.limit?.value || '';
          const unit = record.elapsedTime?.unit || record.limit?.unit || 'Hours';
          
          if (record.elapsedTime?.unit === 'Calendar Days' && elapsedValue) {
            // Remove decimals from Calendar Days elapsed value
            elapsedValue = Math.floor(parseFloat(elapsedValue) || 0).toString();
          }
          if (record.remainingTime?.unit === 'Calendar Days' && remainingValue) {
            // Remove decimals from Calendar Days remaining value
            remainingValue = Math.floor(parseFloat(remainingValue) || 0).toString();
          }
          if (record.limit?.unit === 'Calendar Days' && limitValue) {
            // Remove decimals from Calendar Days limit value
            limitValue = Math.floor(parseFloat(limitValue) || 0).toString();
          }
          
          // Set default limit value based on unit
          let defaultLimitValue = '00:00';
          if (unit === 'Years') {
            defaultLimitValue = '0.00';
          } else if (unit === 'Calendar Days') {
            defaultLimitValue = '0';
          }
          
          const limitObj = record.limit ? {
            ...record.limit,
            value: limitValue || defaultLimitValue
          } : { value: defaultLimitValue, unit: unit };
          const installedUnit = limitObj.unit || unit;
          const airframeVal = record.airframeValueAtInstallation ?? (record.installedValue ? { value: record.installedValue.value ?? '', unit: record.installedValue.unit ?? installedUnit } : { value: '', unit: installedUnit });
          const parentVal = record.parentAssemblyValueAtInstallation ?? { value: '', unit: installedUnit };
          const selfVal = record.assemblySelfValueAtInstallation ?? { value: '', unit: installedUnit };
          return {
            ...record,
            elapsedTime: record.elapsedTime ? {
              ...record.elapsedTime,
              value: elapsedValue
            } : record.elapsedTime,
            remainingTime: record.remainingTime ? {
              ...record.remainingTime,
              value: remainingValue
            } : record.remainingTime,
            limit: limitObj,
            airframeValueAtInstallation: { value: airframeVal.value ?? '', unit: airframeVal.unit ?? installedUnit },
            parentAssemblyValueAtInstallation: { value: parentVal.value ?? '', unit: parentVal.unit ?? installedUnit },
            assemblySelfValueAtInstallation: { value: selfVal.value ?? '', unit: selfVal.unit ?? installedUnit }
          };
        });
        setTimeSinceRecords(recordsWithLimit);
      }
      // Then check selectedAssembly (fallback)
      else if (selectedAssembly && selectedAssembly.timeSinceRecords) {
        const recordsWithLimit = selectedAssembly.timeSinceRecords.map(record => {
          // Clean Calendar Days values to ensure they're integers
          let elapsedValue = record.elapsedTime?.value || '';
          let remainingValue = record.remainingTime?.value || '';
          let limitValue = record.limit?.value || '';
          const unit = record.elapsedTime?.unit || record.limit?.unit || 'Hours';
          
          if (record.elapsedTime?.unit === 'Calendar Days' && elapsedValue) {
            elapsedValue = Math.floor(parseFloat(elapsedValue) || 0).toString();
          }
          if (record.remainingTime?.unit === 'Calendar Days' && remainingValue) {
            remainingValue = Math.floor(parseFloat(remainingValue) || 0).toString();
          }
          if (record.limit?.unit === 'Calendar Days' && limitValue) {
            limitValue = Math.floor(parseFloat(limitValue) || 0).toString();
          }
          
          let defaultLimitValue = '00:00';
          if (unit === 'Years') defaultLimitValue = '0.00';
          else if (unit === 'Calendar Days') defaultLimitValue = '0';
          
          const limitObj = record.limit ? {
            ...record.limit,
            value: limitValue || defaultLimitValue
          } : { value: defaultLimitValue, unit: unit };
          const installedUnit = limitObj.unit || unit;
          const airframeVal = record.airframeValueAtInstallation ?? (record.installedValue ? { value: record.installedValue.value ?? '', unit: record.installedValue.unit ?? installedUnit } : { value: '', unit: installedUnit });
          const parentVal = record.parentAssemblyValueAtInstallation ?? { value: '', unit: installedUnit };
          const selfVal = record.assemblySelfValueAtInstallation ?? { value: '', unit: installedUnit };
          return {
            ...record,
            elapsedTime: record.elapsedTime ? { ...record.elapsedTime, value: elapsedValue } : record.elapsedTime,
            remainingTime: record.remainingTime ? { ...record.remainingTime, value: remainingValue } : record.remainingTime,
            limit: limitObj,
            airframeValueAtInstallation: { value: airframeVal.value ?? '', unit: airframeVal.unit ?? installedUnit },
            parentAssemblyValueAtInstallation: { value: parentVal.value ?? '', unit: parentVal.unit ?? installedUnit },
            assemblySelfValueAtInstallation: { value: selfVal.value ?? '', unit: selfVal.unit ?? installedUnit }
          };
        });
        setTimeSinceRecords(recordsWithLimit);
      } else {
        setTimeSinceRecords([]);
      }
    }
  }, [open, selectedAssembly, formData.timeSinceRecords]);

  // Handle manufacturer change - clear modelId when manufacturer changes
  const handleManufacturerChange = (e) => {
    setFormData({ 
      ...formData, 
      manufacturerNameId: e.target.value,
      modelId: '' // Clear model when manufacturer changes
    });
  };

  // Handle assembly type change - auto-populate ATA Chapter
  const handleAssemblyTypeChange = (event, newValue) => {
    // newValue is the selected assembly type object or null
    if (!newValue) {
      setFormData({
        ...formData,
        type: '',
        typeName: '',
        ataChapterId: '',
        valuesAtInstallation: []
      });
      return;
    }

    const assemblyTypeId = newValue._id ? String(newValue._id) : String(newValue);
    const selectedAssemblyType = newValue;
    const typeName = selectedAssemblyType?.name || selectedAssemblyType?.typeName || '';

    const updatedFormData = { ...formData, type: assemblyTypeId, typeName };

    // Auto-populate ATA Chapter from Assembly Type
    if (selectedAssemblyType && selectedAssemblyType.ataChapterId) {
      let ataChapterId = null;
      
      // Handle populated ataChapterId (object with _id, code, name from API)
      if (typeof selectedAssemblyType.ataChapterId === 'object' && selectedAssemblyType.ataChapterId !== null) {
        // Populated object from MongoDB populate - has _id property
        if (selectedAssemblyType.ataChapterId._id) {
          ataChapterId = String(selectedAssemblyType.ataChapterId._id);
        } else {
          // Fallback: try to use the object itself
          ataChapterId = String(selectedAssemblyType.ataChapterId);
        }
      } else {
        // Unpopulated: just an ID (string or ObjectId)
        ataChapterId = String(selectedAssemblyType.ataChapterId);
      }
      
      if (ataChapterId) {
        updatedFormData.ataChapterId = ataChapterId;
      }
    } else {
      // Clear ATA Chapter if assembly type doesn't have one (e.g., Airframe)
      updatedFormData.ataChapterId = '';
    }

    // Clear valuesAtInstallation and custom config when assembly type changes
    updatedFormData.valuesAtInstallation = [];
    updatedFormData.customColumns = [];
    updatedFormData.customValuesAtInstallation = [];

    setFormData(updatedFormData);
  };

  // Handle configuration value changes
  const handleConfigValueChange = (columnName, field, value) => {
    setFormData(prev => {
      const valuesAtInstallation = prev.valuesAtInstallation || [];
      const existingIndex = valuesAtInstallation.findIndex(
        val => val.columnName === columnName
      );

      if (existingIndex >= 0) {
        // Update existing value
        const updated = [...valuesAtInstallation];
        updated[existingIndex] = {
          ...updated[existingIndex],
          [field]: value
        };
        return {
          ...prev,
          valuesAtInstallation: updated
        };
      } else {
        // Add new value
        return {
          ...prev,
          valuesAtInstallation: [
            ...valuesAtInstallation,
            {
              columnName,
              assemblyValue: field === 'assemblyValue' ? value : '',
              airframeValue: field === 'airframeValue' ? value : ''
            }
          ]
        };
      }
    });
  };

  // Get current value for a configuration column
  const getConfigValue = (columnName, field) => {
    if (!formData.valuesAtInstallation) return '';
    const value = formData.valuesAtInstallation.find(
      val => val.columnName === columnName
    );
    return value ? value[field] || '' : '';
  };

  // Time Since Records handlers
  const handleAddTimeSinceRecord = () => {
    const defaultUnit = 'Hours';
    setTimeSinceRecords([
      ...timeSinceRecords,
      {
        type: 'TSN',
        label: 'Since New (TSN)',
        elapsedTime: { value: '00:00', unit: defaultUnit },
        limit: { value: '00:00', unit: defaultUnit },
        remainingTime: { value: '00:00', unit: defaultUnit },
        airframeValueAtInstallation: { value: '', unit: defaultUnit },
        parentAssemblyValueAtInstallation: { value: '', unit: defaultUnit },
        assemblySelfValueAtInstallation: { value: '', unit: defaultUnit },
        startDate: new Date().toISOString().split('T')[0],
        asOnDate: new Date().toISOString().split('T')[0]
      }
    ]);
  };

  const handleUpdateTimeSinceRecord = (index, field, value) => {
    const updated = [...timeSinceRecords];
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      const prevLimitUnit = updated[index].limit?.unit;
      const prevElapsedUnit = updated[index].elapsedTime?.unit;
      updated[index][parent] = updated[index][parent] || {};
      updated[index][parent][child] = value;

      if (parent === 'remainingTime' && child === 'unit') {
        const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
        const canonical = computeRemainingSnapshot(updated[index]);
        if (canonical) {
          const display = convertRemainingCanonicalToDisplay(
            canonical.value,
            canonical.unit,
            elapsedUnit
          );
          updated[index].remainingTime = {
            value: display.value,
            unit: display.unit
          };
        } else {
          updated[index].remainingTime = {
            ...(updated[index].remainingTime || {}),
            unit: elapsedUnit
          };
        }
      } else {
      // Keep snapshot of this assembly's value at installation in sync with the elapsed value from the form
      if (parent === 'elapsedTime' && child === 'value') {
        const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
        updated[index].assemblySelfValueAtInstallation = updated[index].assemblySelfValueAtInstallation || { value: '', unit: elapsedUnit };
        updated[index].assemblySelfValueAtInstallation.value = value;
        if (!updated[index].assemblySelfValueAtInstallation.unit) {
          updated[index].assemblySelfValueAtInstallation.unit = elapsedUnit;
        }
      }

      const shouldRecalculate = parent === 'limit' || parent === 'elapsedTime' || 
                                field === 'startDate' || field === 'asOnDate' ||
                                (parent === 'elapsedTime' && child === 'unit') ||
                                (parent === 'limit' && child === 'unit');

      if ((parent === 'elapsedTime' && child === 'unit') || (parent === 'limit' && child === 'unit')) {
        const newUnit = value;
        let elapsedUnit;
        
        // If limit.unit changes, only sync elapsed when it matched the previous limit (preserves Years↔Calendar Days mixed rows)
        if (parent === 'limit' && child === 'unit') {
          updated[index].elapsedTime = updated[index].elapsedTime || {};
          const norm = (u) => (u == null || u === '' ? 'Hours' : u);
          const elapsedLinkedToLimit = norm(prevElapsedUnit) === norm(prevLimitUnit);
          if (elapsedLinkedToLimit) {
            updated[index].elapsedTime.unit = newUnit;
          }
          elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
        } else {
          elapsedUnit = newUnit;
        }
        
        updated[index].remainingTime = updated[index].remainingTime || {};
        const limitUnitAfter = updated[index].limit?.unit || 'Hours';
        const elapsedUnitAfter = updated[index].elapsedTime?.unit || 'Hours';
        const mixedYearsCd = isMixedYearsCalendarLimitElapsed(limitUnitAfter, elapsedUnitAfter);
        updated[index].remainingTime.unit = elapsedUnitAfter;
        const syncValAtInstall = (key) => {
          updated[index][key] = updated[index][key] || { value: '', unit: elapsedUnit };
          updated[index][key].unit = elapsedUnit;
        };
        syncValAtInstall('airframeValueAtInstallation');
        syncValAtInstall('parentAssemblyValueAtInstallation');
        syncValAtInstall('assemblySelfValueAtInstallation');

        const applyUnitConversionToVal = (key) => {
          const obj = updated[index][key];
          if (!obj || obj.value == null || obj.value === '') return;
          if (newUnit === 'Hours') {
            if (obj.value && !obj.value.includes(':')) obj.value = '00:00';
          } else if (newUnit === 'Years') {
            if (obj.value && obj.value.includes(':')) obj.value = '0.00';
          } else {
            const isIntegerUnit = (newUnit === 'Calendar Days' || newUnit === 'Landings');
            if (obj.value.includes(':')) obj.value = isIntegerUnit ? '0' : (newUnit === 'Cycles' ? '0' : '0');
            else if (obj.value.includes('.') && isIntegerUnit) obj.value = Math.floor(parseFloat(obj.value) || 0).toString();
          }
        };

        if (newUnit === 'Hours') {
          if (parent === 'elapsedTime' && updated[index].elapsedTime.value && !updated[index].elapsedTime.value.includes(':')) {
            updated[index].elapsedTime.value = '00:00';
          }
          if (parent === 'limit' && updated[index].limit.value && !updated[index].limit.value.includes(':')) {
            updated[index].limit.value = '00:00';
          }
          ['airframeValueAtInstallation', 'parentAssemblyValueAtInstallation', 'assemblySelfValueAtInstallation'].forEach(applyUnitConversionToVal);
          if (parent === 'limit' && updated[index].elapsedTime.value && !updated[index].elapsedTime.value.includes(':')) {
            updated[index].elapsedTime.value = '00:00';
          }
          if (!mixedYearsCd && updated[index].remainingTime.value && !updated[index].remainingTime.value.includes(':')) {
            updated[index].remainingTime.value = '00:00';
          }
        } else if (newUnit === 'Years') {
          if (parent === 'elapsedTime' && updated[index].elapsedTime.value && updated[index].elapsedTime.value.includes(':')) {
            updated[index].elapsedTime.value = '0.00';
          }
          if (parent === 'limit' && updated[index].limit.value && updated[index].limit.value.includes(':')) {
            updated[index].limit.value = '0.00';
          }
          if (parent === 'limit' && updated[index].elapsedTime.value && updated[index].elapsedTime.value.includes(':')) {
            updated[index].elapsedTime.value = '0.00';
          }
          if (!mixedYearsCd && updated[index].remainingTime.value && updated[index].remainingTime.value.includes(':')) {
            updated[index].remainingTime.value = '0.00';
          }
          ['airframeValueAtInstallation', 'parentAssemblyValueAtInstallation', 'assemblySelfValueAtInstallation'].forEach(applyUnitConversionToVal);
        } else {
          // Calendar Days, Cycles, or Landings
          const isIntegerUnit = (newUnit === 'Calendar Days' || newUnit === 'Landings');
          ['airframeValueAtInstallation', 'parentAssemblyValueAtInstallation', 'assemblySelfValueAtInstallation'].forEach(applyUnitConversionToVal);
          if (parent === 'elapsedTime' && updated[index].elapsedTime.value) {
            if (updated[index].elapsedTime.value.includes(':')) {
              updated[index].elapsedTime.value = isIntegerUnit ? '0' : (newUnit === 'Cycles' ? '0' : '0');
            } else if (updated[index].elapsedTime.value.includes('.') && isIntegerUnit) {
              updated[index].elapsedTime.value = Math.floor(parseFloat(updated[index].elapsedTime.value) || 0).toString();
            }
            // Cycles: keep decimals, only convert from Hours/Years
          }
          if (parent === 'limit' && updated[index].limit.value) {
            if (updated[index].limit.value.includes(':')) {
              updated[index].limit.value = '0';
            } else if (updated[index].limit.value.includes('.') && isIntegerUnit) {
              updated[index].limit.value = Math.floor(parseFloat(updated[index].limit.value) || 0).toString();
            }
          }
          if (parent === 'limit' && updated[index].elapsedTime.value) {
            if (updated[index].elapsedTime.value.includes(':')) {
              updated[index].elapsedTime.value = '0';
            } else if (updated[index].elapsedTime.value.includes('.') && isIntegerUnit) {
              updated[index].elapsedTime.value = Math.floor(parseFloat(updated[index].elapsedTime.value) || 0).toString();
            }
          }
          if (!mixedYearsCd && updated[index].remainingTime.value) {
            if (updated[index].remainingTime.value.includes(':')) {
              updated[index].remainingTime.value = '0';
            } else if (updated[index].remainingTime.value.includes('.') && isIntegerUnit) {
              updated[index].remainingTime.value = Math.floor(parseFloat(updated[index].remainingTime.value) || 0).toString();
            }
          }
        }
      }

      if (shouldRecalculate) {
        const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
        const limitUnit = updated[index].limit?.unit || 'Hours';
        const canonical = computeRemainingSnapshot(updated[index]);
        if (canonical) {
          const display = convertRemainingCanonicalToDisplay(
            canonical.value,
            canonical.unit,
            elapsedUnit
          );
          updated[index].remainingTime = {
            value: display.value,
            unit: display.unit
          };
        }
      }
      }
    } else {
      updated[index][field] = value;
      
      if (field === 'startDate' || field === 'asOnDate') {
        const elapsedUnit = updated[index].elapsedTime?.unit || 'Hours';
        const limitUnit = updated[index].limit?.unit || 'Hours';

        if (elapsedUnit === 'Years' && updated[index].startDate && updated[index].asOnDate) {
          const elapsedValueStr = calculateYearsFromDates(updated[index].startDate, updated[index].asOnDate);
          updated[index].elapsedTime = {
            value: elapsedValueStr,
            unit: elapsedUnit
          };

          const canonical = computeRemainingSnapshot(updated[index]);
          if (canonical) {
            const display = convertRemainingCanonicalToDisplay(
              canonical.value,
              canonical.unit,
              elapsedUnit
            );
            updated[index].remainingTime = {
              value: display.value,
              unit: display.unit
            };
          }
        } else if (elapsedUnit === 'Calendar Days' && updated[index].startDate && updated[index].asOnDate) {
          const elapsedValueStr = calculateCalendarDaysFromDates(updated[index].startDate, updated[index].asOnDate);
          updated[index].elapsedTime = {
            value: elapsedValueStr,
            unit: elapsedUnit
          };

          const canonical = computeRemainingSnapshot(updated[index]);
          if (canonical) {
            const display = convertRemainingCanonicalToDisplay(
              canonical.value,
              canonical.unit,
              elapsedUnit
            );
            updated[index].remainingTime = {
              value: display.value,
              unit: display.unit
            };
          }
        }
      }
    }
    setTimeSinceRecords(updated);
    setFormData({ ...formData, timeSinceRecords: updated });
  };

  const handleRemoveTimeSinceRecord = (index) => {
    const updated = timeSinceRecords.filter((_, i) => i !== index);
    setTimeSinceRecords(updated);
    setFormData({ ...formData, timeSinceRecords: updated });
  };

  // Navigate to Master Data Management to add Assembly Type
  const handleNavigateToAddAssemblyType = () => {
    const confirmed = window.confirm(
      'You will be taken to Master Data Management to add a new Assembly Type.\n\n' +
      'After adding the Assembly Type, please return to this page - the dropdown will be refreshed automatically.'
    );
    
    if (confirmed) {
      // Navigate to master data with state to open Assembly Types tab (index 2)
      navigate('/master-data', { 
        state: { 
          openTab: 2, // Assembly Types tab index
          returnTo: '/aircraft-lifecycle',
          refreshDropdowns: true
        } 
      });
    }
  };

  // Navigate to Parts Catalog to add Part
  const handleNavigateToAddPart = () => {
    const confirmed = window.confirm(
      'You will be taken to Parts Catalog to add a new Part.\n\n' +
      'After adding the Part, please return to this form - the dropdown will be refreshed automatically.'
    );
    if (confirmed) {
      navigate('/parts-catalog', { 
        state: { 
          returnTo: '/aircraft-lifecycle',
          refreshDropdowns: true
        } 
      });
    }
  };

  /**
   * Handle Part Master selection from Autocomplete (selected option object).
   * Uses the selected part object directly so selection works for parts found via search,
   * and uses functional update to avoid stale closure.
   */
  const handlePartMasterSelect = React.useCallback((selectedPart) => {
    if (!selectedPart) {
      setFormData(prev => ({
        ...prev,
        partId: '',
        partNumber: '',
        partName: ''
      }));
      return;
    }
    const partId = selectedPart._id?.toString() || selectedPart._id;
    setFormData(prev => ({
      ...prev,
      partId,
      partNumber: selectedPart.partNumber || '',
      partName: (selectedPart.partName && String(selectedPart.partName).trim()) || ''
    }));
  }, [setFormData]);

  // Custom assembly type configuration (when no master config): column definitions and values
  const handleAddCustomColumn = () => {
    const columns = Array.isArray(formData.customColumns) ? [...formData.customColumns] : [];
    columns.push({
      name: '',
      label: '',
      type: 'text',
      unit: '',
      required: false,
      order: columns.length + 1
    });
    setFormData(prev => ({ ...prev, customColumns: columns }));
  };

  const handleRemoveCustomColumn = (index) => {
    const columns = Array.isArray(formData.customColumns) ? [...formData.customColumns] : [];
    if (columns.length <= 1) return;
    const next = columns.filter((_, i) => i !== index).map((col, i) => ({ ...col, order: i + 1 }));
    const removedName = columns[index]?.name;
    let values = Array.isArray(formData.customValuesAtInstallation) ? [...formData.customValuesAtInstallation] : [];
    if (removedName) {
      values = values.filter(v => v.columnName !== removedName);
    }
    setFormData(prev => ({ ...prev, customColumns: next, customValuesAtInstallation: values }));
  };

  const handleCustomColumnChange = (index, field, value) => {
    const columns = Array.isArray(formData.customColumns) ? [...formData.customColumns] : [];
    if (index < 0 || index >= columns.length) return;
    const next = columns.map((col, i) =>
      i === index ? { ...col, [field]: field === 'order' ? (typeof value === 'number' ? value : parseInt(value, 10) || index + 1) : value } : col
    );
    setFormData(prev => ({ ...prev, customColumns: next }));
  };

  const handleCustomColumnApplyTemplate = (index, template) => {
    if (!template || !template.name) return;
    setFormData(prev => {
      const columns = Array.isArray(prev.customColumns) ? [...prev.customColumns] : [];
      if (index < 0 || index >= columns.length) return prev;
      const next = columns.map((col, i) =>
        i === index
          ? { ...col, name: template.name, label: template.label || template.name, type: template.type || 'text', unit: template.unit || '' }
          : col
      );
      return { ...prev, customColumns: next };
    });
  };

  const getCustomConfigValue = (columnName, field) => {
    const values = formData.customValuesAtInstallation || [];
    const entry = values.find(v => v.columnName === columnName);
    return entry ? (entry[field] || '') : '';
  };

  const handleCustomConfigValueChange = (columnName, field, value) => {
    setFormData(prev => {
      const values = prev.customValuesAtInstallation || [];
      const idx = values.findIndex(v => v.columnName === columnName);
      let next;
      if (idx >= 0) {
        next = values.map((v, i) => i === idx ? { ...v, [field]: value } : v);
      } else {
        next = [...values, { columnName, assemblyValue: field === 'assemblyValue' ? value : '', airframeValue: field === 'airframeValue' ? value : '' }];
      }
      return { ...prev, customValuesAtInstallation: next };
    });
  };

  return {
    // State
    assemblyTypeConfig,
    configLoading,
    timeSinceRecords,
    filteredModelVariants,
    
    // Handlers
    handleManufacturerChange,
    handleAssemblyTypeChange,
    handleConfigValueChange,
    getConfigValue,
    handleAddTimeSinceRecord,
    handleUpdateTimeSinceRecord,
    handleRemoveTimeSinceRecord,
    handleNavigateToAddAssemblyType,
    handleNavigateToAddPart,
    handlePartMasterSelect,
    handleAddCustomColumn,
    handleRemoveCustomColumn,
    handleCustomColumnChange,
    handleCustomColumnApplyTemplate,
    getCustomConfigValue,
    handleCustomConfigValueChange,
    
    // Utility functions exposed for components
    validateYearsInput,
    validateCalendarDaysInput,
    calculateEndDate,
    prepareTimeSinceRecordsForSubmission
  };
};
